/**
 * MyTargetsWidget — Compact read-only performance-targets card for broker dashboard
 *
 * Shown on the broker's main dashboard when they have >= 1 active target.
 * No edit controls — admins manage targets via Broker Commission Settings
 * → Performance Targets tab.
 *
 * Access: broker-family role viewing their own data. Admins viewing their
 * own dashboard (companyId-wide view) don't see this widget — if they want
 * to review a broker's targets they go to Commission Settings.
 */

import React, { useMemo } from 'react';
import {
  Target,
  Award,
  DollarSign,
  Vault,
  Handshake,
  HandCoins,
  TrendingUp,
  CheckCircle2,
  Activity,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBrokerTargets } from '../../hooks/useBrokerTargets';
import type { CommissionComponent } from '../../hooks/useBrokerTargets';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';

interface MyTargetsWidgetProps {
  companyId: string | undefined;
  brokerDocId: string | undefined;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  loan_book_value: 'Loan Book Value',
  settlement_volume: 'Settlement Volume',
  commission_earned: 'Commission Earned',
};

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

/** One compact rule app-wide (three significant figures); see formatters.ts. */
const formatLargeAmount = (cents: number): string => formatCompactCurrency(cents);

function commissionSubtitle(components?: CommissionComponent[]): string | null {
  if (!components || components.length === 0) return null;
  const upfront = components.includes('upfront');
  const trail = components.includes('trail');
  if (upfront && trail) return 'Upfront + Trail';
  if (upfront) return 'Upfront';
  if (trail) return 'Trail';
  return null;
}

function iconFor(
  type: string,
  components?: CommissionComponent[],
): { Icon: React.ComponentType<{ className?: string }>; color: string; bg: string } {
  switch (type) {
    case 'loan_book_value':
      return { Icon: Vault, color: 'text-amber-400', bg: 'rgba(245, 158, 11, 0.15)' };
    case 'settlement_volume':
      return { Icon: Handshake, color: 'text-emerald-400', bg: 'rgba(16, 185, 129, 0.15)' };
    case 'commission_earned': {
      // Single component → component's color. Both → default to upfront blue.
      if (components?.length === 1 && components[0] === 'trail') {
        return { Icon: TrendingUp, color: 'text-purple-400', bg: 'rgba(168, 85, 247, 0.15)' };
      }
      return { Icon: HandCoins, color: 'text-blue-400', bg: 'rgba(59, 130, 246, 0.15)' };
    }
    default:
      return { Icon: Target, color: 'text-blue-400', bg: 'rgba(59, 130, 246, 0.15)' };
  }
}

const MyTargetsWidget: React.FC<MyTargetsWidgetProps> = ({ companyId, brokerDocId }) => {
  const navigate = useNavigate();
  const { targets, progress, totalBonusPayableCents, loading } = useBrokerTargets(
    companyId,
    brokerDocId,
  );

  const targetCards = useMemo(() => {
    return targets.map((t) => {
      const p = progress.find((pr) => pr.targetId === t.id);
      return {
        ...t,
        currentValueCents: p?.currentValueCents ?? 0,
        isMet: p?.isMet ?? false,
        percentComplete: p?.percentComplete ?? 0,
        periodLabel: p?.periodLabel,
      };
    });
  }, [targets, progress]);

  const metCount = targetCards.filter((t) => t.isMet).length;

  // Don't render if the broker has no active targets — zero-data state would
  // add clutter to the dashboard. Admins can seed targets via Commission
  // Settings; at that point the widget appears naturally on next page load.
  if (!loading && targets.length === 0) return null;
  if (!companyId || !brokerDocId) return null;

  return (
    <div
      className="rounded-lg shadow-xl overflow-hidden relative border border-gray-700"
      style={{
        background:
          'linear-gradient(135deg, rgba(31,41,55,0.9) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)',
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ background: 'rgba(59, 130, 246, 0.15)' }}
            >
              <Target className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold uppercase tracking-wider">
                My Performance Targets
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {loading
                  ? 'Loading…'
                  : `${metCount} of ${targets.length} met${
                      totalBonusPayableCents > 0
                        ? ` • ${formatCurrency(totalBonusPayableCents)} bonus payable`
                        : ''
                    }`}
              </p>
            </div>
          </div>

          {/* Total bonus pill (stands out when > 0) */}
          {totalBonusPayableCents > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30">
              <Award className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs font-bold text-green-400">
                {formatCurrency(totalBonusPayableCents)}
              </span>
            </div>
          )}
        </div>

        {/* Targets grid */}
        {loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading targets…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {targetCards.map((target) => {
              const { Icon, color, bg } = iconFor(target.type, target.commissionComponents);
              const pct = Math.min(target.percentComplete, 100);
              const barColor = target.isMet ? 'bg-green-500' : 'bg-amber-500';

              return (
                <div
                  key={target.id}
                  className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 hover:bg-gray-900/80 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <div
                        className="p-1.5 rounded flex-shrink-0"
                        style={{ background: bg }}
                      >
                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-xs font-semibold truncate">
                          {target.label}
                        </p>
                        <p className="text-gray-500 text-[0.625rem] mt-0.5">
                          {TARGET_TYPE_LABELS[target.type] || target.type}
                          {commissionSubtitle(target.commissionComponents) && (
                            <> ({commissionSubtitle(target.commissionComponents)})</>
                          )}{' '}
                          •{' '}
                          {target.periodLabel ||
                            PERIOD_LABELS[target.period] ||
                            target.period}
                          {target.loanBookLabel && <> • {target.loanBookLabel}</>}
                        </p>
                      </div>
                    </div>

                    {target.isMet ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wider rounded bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">
                        <CheckCircle2 className="w-2.5 h-2.5" />
                        Met
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wider rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex-shrink-0">
                        <Activity className="w-2.5 h-2.5" />
                        {target.percentComplete.toFixed(0)}%
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full ${barColor} rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  <div className="flex items-baseline justify-between">
                    <span className="text-[0.6875rem] text-gray-500">
                      <span className="text-gray-300 font-semibold">
                        {formatLargeAmount(target.currentValueCents)}
                      </span>{' '}
                      / {formatLargeAmount(target.thresholdCents)}
                    </span>
                    <span
                      className={`text-[0.6875rem] font-semibold flex items-center gap-0.5 ${
                        target.isMet ? 'text-green-400' : 'text-gray-500'
                      }`}
                    >
                      <DollarSign className="w-2.5 h-2.5" />
                      {formatLargeAmount(target.bonusCents)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTargetsWidget;
